import React, { useState, useEffect, useGlobal, useDispatch } from "reactn";
import axios from "axios";
import { FiRefreshCw } from "react-icons/fi";
import BaseModal from "./baseModal/BaseModal";
import {
  SWITCH,
  GET_HTML,
  CONTENT_BLOCK,
  IMAGE_DROPDOWN
} from "./baseModal/FieldTypes";
import { toast } from "react-toastify";
import ReshuffleModal from "./ReshuffleModal";
import { PartyIcon, FieldTripIcon, PrivateEventIcon } from "../../assets";

const AIPartyInspirationModal = ({ onClose, history }) => {
  const global = useGlobal()[0];
  const setGlobal = useGlobal()[1];
  const [loading, setLoading] = useGlobal("loading");
  const dispatch = useDispatch();

  const [showReshuffleModal, setShowReshuffleModal] = useState(false);
  const [reshuffleMode, setReshuffleMode] = useState("name");

  const [superlativeData, setSuperlativeData] = useState({
    mostProfitable: [],
    mostEngaged: [],
    mostUsedSettings: {
      targetChildren: true,
      applicationRequired: false,
      offlinePayments: false
    }
  });

  const [partyTitle, setPartyTitle] = useState("Activating magic wizard...");
  const [partyDescription, setPartyDescription] = useState(
    "Activating magic wizard..."
  );
  const [partyImage, setPartyImage] = useState("");

  const [isGeneratingSuggestions, setIsGeneratingSuggestions] = useState(false);
  const [ellipsis, setEllipsis] = useState("...");
  const [isCreating, setIsCreating] = useState(false);

  const [partyType, setPartyType] = useState("");
  const [addChild, setAddChild] = useState(false);
  const [manualInvoices, setManualInvoices] = useState(false);
  const [allowSameEnrollment, setAllowSameEnrollment] = useState(true);
  const [birthdayParty] = useState(true);

  useEffect(() => {
    if (isGeneratingSuggestions) {
      let i = 0;
      const seq = [".", "..", "..."];
      const id = setInterval(() => {
        i = (i + 1) % seq.length;
        setEllipsis(seq[i]);
      }, 500);
      return () => clearInterval(id);
    } else {
      setEllipsis("...");
    }
  }, [isGeneratingSuggestions]);

  useEffect(() => {
    if (global.organizationInfo && global.organizationInfo.partnerId) {
      loadAIAnalysis();
      generateAISuggestions();
    }
  }, [global.organizationInfo]);

  const loadAIAnalysis = async () => {
    try {
      if (!global.organizationInfo || !global.organizationInfo.partnerId) {
        console.warn("Partner ID not available yet");
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API}/partners/parties/ai-analysis/${global.organizationInfo.partnerId}`
      );

      if (response.data.success) {
        const analysisData = response.data.data;
        updateSuggestionsFromAnalysis(analysisData);
      }
    } catch (error) {
      console.error("Error loading AI analysis:", error);
    }
  };

  const updateSuggestionsFromAnalysis = analysisData => {
    if (!analysisData) return;

    if (Array.isArray(analysisData.mostProfitable)) {
      setSuperlativeData(prev => ({
        ...prev,
        mostProfitable: analysisData.mostProfitable
      }));
    }

    if (Array.isArray(analysisData.mostEngaged)) {
      setSuperlativeData(prev => ({
        ...prev,
        mostEngaged: analysisData.mostEngaged
      }));
    }

    if (analysisData.mostUsedSettings) {
      setSuperlativeData(prev => ({
        ...prev,
        mostUsedSettings: {
          targetChildren: analysisData.mostUsedSettings.targetChildren || true,
          offlinePayments:
            analysisData.mostUsedSettings.offlinePayments || false
        }
      }));
    }
  };

  const generateAISuggestions = async (constraints = {}) => {
    setIsGeneratingSuggestions(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API}/partners/parties/openai-suggestions`,
        {
          partnerId: global.organizationInfo.partnerId,
          ...constraints
        }
      );

      if (response.data.success) {
        const s = response.data.data;
        setPartyTitle(s.partyTitle);
        setPartyDescription(s.partyDescription);
        setPartyImage(s.partyImage);

        setPartyType(s.partyType || "Party");
        setAddChild(s.targetChildren);
        setManualInvoices(s.offlinePayments);

        toast.success("AI suggestions generated successfully!");
      } else {
        toast.error(
          response.data.message || "Failed to generate AI suggestions"
        );
      }
    } catch (error) {
      console.error("Error generating AI suggestions:", error);
      toast.error("Failed to generate AI suggestions. Please try again.");
    } finally {
      setIsGeneratingSuggestions(false);
    }
  };

  const shuffleSuggestions = () => {
    generateAISuggestions({ isShuffleRequest: true });
  };

  const shuffleSpecificField = async fieldType => {
    if (fieldType === "name" || fieldType === "description") {
      setReshuffleMode(fieldType === "name" ? "name" : "description");
      setShowReshuffleModal(true);
      return;
    }

    if (fieldType === "image") {
      setIsGeneratingSuggestions(true);
      try {
        const resp = await axios.post(
          `${process.env.REACT_APP_API}/partners/parties/openai-suggestions`,
          {
            partnerId: global.organizationInfo.partnerId,
            isShuffleRequest: true,
            shuffleType: "image"
          }
        );
        if (resp.data.success) {
          const s = resp.data.data;
          setPartyImage(s.partyImage);
          toast.success("Party image shuffled!");
        } else {
          toast.error("Failed to shuffle. Please try again.");
        }
      } catch (e) {
        console.error("Error shuffling image:", e);
        toast.error("Failed to shuffle. Please try again.");
      } finally {
        setIsGeneratingSuggestions(false);
      }
    }
  };

  const handleApplyGeneratedName = selectedName => {
    setPartyTitle(selectedName);
    toast.success("Party title applied!");
  };

  const handleApplyGeneratedDescription = selectedDesc => {
    setPartyDescription(selectedDesc);
    toast.success("Party description applied!");
  };

  const handleImageUpload = async e => {
    if (e.target.files.length && e.target.files[0]) {
      const fd = new FormData();
      fd.set("image", e.target.files[0]);
      const ep = `${process.env.REACT_APP_API}/partners/forms/document`;
      setLoading(true);
      try {
        const result = await axios.post(ep, fd);
        if (result.data.success) {
          setPartyImage(result.data.data);
        }
      } catch (error) {
        console.error("Error uploading image:", error);
      }
      setLoading(false);
    }
  };

  const partyTitleWithShuffle = () => (
    <div style={{ marginBottom: "20px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "8px"
        }}
      >
        <div className="field-component-title">Party Title</div>
        <button
          onClick={() => shuffleSpecificField("name")}
          disabled={isGeneratingSuggestions}
          style={{
            padding: "4px 8px",
            backgroundColor: "#6c757d",
            color: "#fff",
            border: "none",
            borderRadius: "8px",
            cursor: isGeneratingSuggestions ? "not-allowed" : "pointer",
            fontSize: "11px",
            whiteSpace: "nowrap",
            opacity: isGeneratingSuggestions ? 0.6 : 1
          }}
        >
          <span
            style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
          >
            <FiRefreshCw />
            Shuffle
          </span>
        </button>
      </div>
      <input
        type="text"
        value={
          isGeneratingSuggestions &&
          /^activating magic wizard/i.test(partyTitle)
            ? `Activating magic wizard${ellipsis}`
            : partyTitle
        }
        onChange={e => setPartyTitle(e.target.value)}
        placeholder="Enter party title"
        style={{
          width: "100%",
          padding: "8px 12px",
          border: "1px solid #ccc",
          borderRadius: "4px",
          fontSize: "14px"
        }}
      />
    </div>
  );

  const partyDescriptionWithShuffle = () => (
    <div style={{ marginBottom: "20px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "8px"
        }}
      >
        <div className="field-component-title">Party Description</div>
        <button
          onClick={() => shuffleSpecificField("description")}
          disabled={isGeneratingSuggestions}
          style={{
            padding: "4px 8px",
            backgroundColor: "#6c757d",
            color: "#fff",
            border: "none",
            borderRadius: "8px",
            cursor: isGeneratingSuggestions ? "not-allowed" : "pointer",
            fontSize: "11px",
            whiteSpace: "nowrap",
            opacity: isGeneratingSuggestions ? 0.6 : 1
          }}
        >
          <span
            style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
          >
            <FiRefreshCw />
            Shuffle
          </span>
        </button>
      </div>
      <textarea
        value={
          isGeneratingSuggestions &&
          /^activating magic wizard/i.test(partyDescription)
            ? `Activating magic wizard${ellipsis}`
            : partyDescription
        }
        onChange={e => setPartyDescription(e.target.value)}
        placeholder="Enter party description"
        style={{
          width: "100%",
          padding: "8px 12px",
          border: "1px solid #ccc",
          borderRadius: "4px",
          fontSize: "14px",
          minHeight: "120px",
          resize: "vertical"
        }}
      />
    </div>
  );

  const partyImageWithShuffle = () => (
    <div style={{ marginBottom: "20px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "8px"
        }}
      >
        <div className="field-component-title">Party Image</div>
        <button
          onClick={() => shuffleSpecificField("image")}
          disabled={isGeneratingSuggestions || isCreating}
          style={{
            padding: "4px 8px",
            backgroundColor: "#6c757d",
            color: "#fff",
            border: "none",
            borderRadius: "8px",
            cursor:
              isGeneratingSuggestions || isCreating ? "not-allowed" : "pointer",
            fontSize: "11px",
            whiteSpace: "nowrap",
            opacity: isGeneratingSuggestions || isCreating ? 0.6 : 1
          }}
        >
          <span
            style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
          >
            <FiRefreshCw />
            Shuffle
          </span>
        </button>
      </div>

      <div
        style={{
          width: "100%",
          minHeight: 200,
          border: "1px solid #e3e3e3",
          borderRadius: 8,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          background: "#fafafa"
        }}
      >
        {partyImage ? (
          <img
            src={partyImage}
            alt="Party preview"
            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "cover" }}
          />
        ) : (
          <div style={{ color: "#999", fontSize: 12 }}>
            No image yet. Click Shuffle to generate.
          </div>
        )}
      </div>
    </div>
  );

  const renderTopProfitable = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {superlativeData.mostProfitable.length === 0 ? (
        <div style={{ fontSize: "12px", color: "#999", italic: "true" }}>
          No data available
        </div>
      ) : (
        superlativeData.mostProfitable.slice(0, 3).map((p, i) => (
          <div
            key={`prof-${i}`}
            style={{
              paddingBottom: "8px",
              borderBottom: i < 2 ? "1px solid #eee" : "none"
            }}
          >
            <div
              style={{
                fontWeight: "bold",
                color: "#333",
                fontSize: "13px",
                marginBottom: "2px"
              }}
            >
              {p.name}
            </div>
            <div
              style={{
                fontSize: "12px",
                color: "#666",
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "4px"
              }}
            >
              <span>
                <strong style={{ color: "#444" }}>Category:</strong>{" "}
                {p.category}
              </span>
              <span>
                <strong style={{ color: "#444" }}>Enrollments:</strong>{" "}
                {p.enrollment || 0}
              </span>
              <span>
                <strong style={{ color: "#444" }}>Revenue:</strong>{" "}
                {p.revenue ? `$${(p.revenue / 100).toLocaleString()}` : "$0"}
              </span>
            </div>
          </div>
        ))
      )}
    </div>
  );

  const renderTopEngaged = () => (
    <div
      style={{
        fontSize: "12px",
        color: "#666",
        fontWeight: "normal",
        display: "flex",
        flexDirection: "column",
        gap: "12px"
      }}
    >
      {superlativeData.mostEngaged.length === 0 ? (
        <div style={{ color: "#999", fontStyle: "italic" }}>
          No data available
        </div>
      ) : (
        superlativeData.mostEngaged.slice(0, 3).map((p, i) => (
          <div
            key={`eng-${i}`}
            style={{
              paddingBottom: "8px",
              borderBottom: i < 2 ? "1px solid #eee" : "none"
            }}
          >
            <div
              style={{ fontWeight: "bold", color: "#333", marginBottom: "2px" }}
            >
              {p.name}
            </div>

            <div>
              <span style={{ marginRight: "8px" }}>
                <strong style={{ color: "#444" }}>Category:</strong>{" "}
                {p.category}
              </span>
              <span>
                | <strong style={{ color: "#444" }}>Enrollments:</strong>{" "}
                {p.enrollment || 0}
              </span>
            </div>
          </div>
        ))
      )}
    </div>
  );

  const renderMostUsed = () => {
    const s = superlativeData.mostUsedSettings;
    return (
      <div style={{ fontSize: "12px", color: "#666", lineHeight: "1.6" }}>
        <div>
          <strong style={{ color: "#444" }}>Target Children:</strong>{" "}
          {s.targetChildren ? "Yes" : "No"}
        </div>
        <div>
          <strong style={{ color: "#444" }}>Offline Payments:</strong>{" "}
          {s.offlinePayments ? "Yes" : "No"}
        </div>
      </div>
    );
  };

  const getSuperlativeFields = () => {
    const hasRealData =
      (superlativeData.mostProfitable || []).length > 0 ||
      (superlativeData.mostEngaged || []).length > 0;

    if (!hasRealData) {
      return [
        {
          type: CONTENT_BLOCK,
          data: {
            name: "Organization Profile",
            content:
              "Since this is a new organization with no existing parties, AI suggestions will be based on your organizational profile and mission.",
            textStyles: {
              fontSize: "12px",
              color: "#666",
              fontWeight: "normal"
            }
          }
        }
      ];
    }

    return [
      {
        type: CONTENT_BLOCK,
        data: {
          name: "Highest Revenue Party Examples",
          content: "",
          textStyles: { fontSize: "12px", color: "#666", fontWeight: "normal" }
        }
      },
      {
        type: GET_HTML,
        data: { gethtml: renderTopProfitable }
      },
      {
        type: CONTENT_BLOCK,
        data: {
          name: "Most Engaged Party Examples",
          content: "",
          textStyles: { fontSize: "12px", color: "#666", fontWeight: "normal" }
        }
      },
      {
        type: GET_HTML,
        data: { gethtml: renderTopEngaged }
      },
      {
        type: CONTENT_BLOCK,
        data: {
          name: "Most Used Settings",
          content: "",
          textStyles: { fontSize: "12px", color: "#666", fontWeight: "normal" }
        }
      },
      {
        type: GET_HTML,
        data: { gethtml: renderMostUsed }
      }
    ];
  };

  const getSettingsFields = () => [
    {
      type: IMAGE_DROPDOWN,
      data: {
        name: "Party Type",
        required: true,
        placeholder: "Pick a type",
        handleChange: value => {
          if (value === "Party" || value === "Field Trip") setAddChild(true);
          else setAddChild(false);
          setPartyType(value);
        },
        choices: [
          {
            text: "Party",
            value: "Party",
            key: 1,
            image: {
              bordered: true,
              src: PartyIcon
            }
          },
          {
            text: "Field Trip",
            value: "Field Trip",
            key: 2,
            image: {
              bordered: true,
              src: FieldTripIcon
            }
          },
          {
            text: "Private Event",
            value: "Private Event",
            key: 3,
            image: {
              bordered: true,
              src: PrivateEventIcon
            }
          }
        ],
        value: partyType
      }
    },
    {
      type: SWITCH,
      data: {
        name: "Target Children",
        checked: addChild,
        required: false,
        handleClick: () => setAddChild(!addChild)
      }
    },
    {
      type: SWITCH,
      data: {
        name: "Offline Payments",
        checked: manualInvoices,
        required: false,
        info: "Allows your client to pass through checkout without paying online",
        handleClick: () => setManualInvoices(!manualInvoices)
      }
    },
    { type: GET_HTML, data: { gethtml: partyTitleWithShuffle } },
    { type: GET_HTML, data: { gethtml: partyDescriptionWithShuffle } },
    { type: GET_HTML, data: { gethtml: partyImageWithShuffle } }
  ];

  const handleSubmit = async () => {
    let s3PhotoKey = null;
    setIsCreating(true);
    try {
      if (partyImage && /^https?:\/\//i.test(partyImage)) {
        const resp = await axios.post(
          `${process.env.REACT_APP_API}/partners/parties/import-photo-from-url`,
          { imageUrl: partyImage }
        );
        if (resp.data?.success && resp.data.data?.party_image) {
          s3PhotoKey = resp.data.data.party_image;
        }
      }
    } catch (e) {
      console.error("Failed to import AI image to S3:", e);
    }

    // Set ONLY the AI-generated content (title, description, image)
    // Let the normal party creation flow handle all settings and initialization
    setGlobal({
      party: {
        ...global.party,
        party_title: partyTitle,
        description: partyDescription,
        party_image: s3PhotoKey || global.party.party_image || null
      }
    });

    history.push({
      pathname: "/partners/parties/setup",
      state: {
        partyType: partyType,
        addChild: addChild,
        manualInvoices: manualInvoices,
        allowSameEnrollment: allowSameEnrollment,
        birthdayParty: birthdayParty,
        fromAIModal: true
      }
    });
    setIsCreating(false);
  };

  const getButtons = () => {
    const createButton = {
      name: isCreating ? "Creating..." : "Create",
      handleClick: handleSubmit,
      buttonColour: "#3B4EFF",
      textColour: "#fff",
      disabled: isCreating || isGeneratingSuggestions
    };
    const shuffleButton = {
      name: isGeneratingSuggestions ? "Shuffling..." : "Grand Shuffle",
      handleClick: shuffleSuggestions,
      buttonColour: "#6c757d",
      textColour: "#fff",
      disabled: isGeneratingSuggestions || isCreating
    };
    return [shuffleButton, createButton];
  };

  const getBaseModalProps = () => ({
    title: "AI Party Inspiration",
    fields: [...getSuperlativeFields(), ...getSettingsFields()],
    buttons: getButtons(),
    handleClose: onClose,
    height: "85vh",
    midSectionHeight: "58vh"
  });

  return (
    <>
      <BaseModal {...getBaseModalProps()} />
      {showReshuffleModal && (
        <ReshuffleModal
          onClose={() => setShowReshuffleModal(false)}
          onApply={
            reshuffleMode === "name"
              ? handleApplyGeneratedName
              : handleApplyGeneratedDescription
          }
          currentValue={
            reshuffleMode === "name" ? partyTitle : partyDescription
          }
          mode={reshuffleMode}
          listingType="parties"
        />
      )}
    </>
  );
};

export default AIPartyInspirationModal;
